import { useState } from "react";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import { getRequestsVisibilityLabel } from "@/utils/requests-visibility";
import type {
  ApplicantProfile,
  AssociationItem,
} from "./associations-list-modal.types";
import {
  formatDate,
  getPersonName,
  isAssociationActive,
  statusBadge,
  unlinkedByText,
} from "./associations-list-modal.helpers";

export const ExpandableRow = ({
  item,
  person,
  direction,
  onUnlink,
}: {
  item: AssociationItem;
  person: ApplicantProfile;
  direction: "representative" | "represented";
  onUnlink?: (item: AssociationItem) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const name = getPersonName(person);
  const isUnlinked = item.authorization_status === "UNLINKED";
  const requestsVisibilityLabel = getRequestsVisibilityLabel(
    item.requests_visibility,
  );

  return (
    <div className="border-b border-neutral-quiet last:border-b-0">
      {/* Row header */}
      <button
        type="button"
        className="w-full flex items-center gap-3 px-6 py-4 hover:bg-neutral-background transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="size-8 rounded-lg bg-surface-action-accent-quiet flex items-center justify-center shrink-0 border-[0.5px] border-action-quiet">
          <Icon type="fas" name="user" className="text-xs text-action-quiet" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-body-loud">{name}</span>
          <span className="text-sm text-body ml-2">{person.email || ""}</span>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <span className="text-sm text-body-quiet">Asociación:</span>
          {statusBadge(
            item.authorization_status,
            item.authorization_status_label,
          )}
          {/* Fixed slots keep the columns aligned whatever the dates measure. */}
          <span className="text-sm text-body-quiet min-w-[13rem]">
            Desde:{" "}
            <span className="text-body-loud">
              {formatDate(item.authorization_approved_at || item.created_at)}
            </span>
          </span>
          <p className="text-sm text-body-quiet min-w-[13rem]">
            <span>Hasta: </span>
            <span className="text-body-loud">
              {formatDate(item.authorization_ended_at)}
            </span>
          </p>
          <div className="size-8 rounded-lg bg-neutral border border-neutral-quiet flex items-center justify-center shrink-0">
            <Icon
              type="far"
              name={expanded ? "chevron-up" : "chevron-down"}
              className="text-xs text-body"
            />
          </div>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-6 pb-4">
          <div className="bg-neutral-object-accent-silent border border-neutral-quiet rounded-lg py-4 flex flex-col gap-3">
            {/* Person header */}
            <div className="px-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-body-loud">{name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-body">
                    {person.email || ""}
                  </span>
                  <Badge
                    size="xs"
                    variant={
                      person.registration_phase === "PRE_REGISTERED"
                        ? "warning"
                        : "action"
                    }
                  >
                    {person.registration_phase === "PRE_REGISTERED"
                      ? "No posee cuenta"
                      : "Registrado"}
                  </Badge>
                </div>
              </div>
              {onUnlink && isAssociationActive(item) && (
                <button
                  type="button"
                  onClick={() => onUnlink(item)}
                  className="flex items-center gap-1.5 text-sm font-medium text-danger hover:text-danger-quiet cursor-pointer shrink-0"
                >
                  <Icon type="far" name="link-slash" className="text-sm" />
                  {direction === "representative"
                    ? "Desvincular representante"
                    : "Desvincular representación"}
                </button>
              )}
            </div>
            <div className="w-full h-px border-t border-neutral-quiet" />

            {/* Details */}
            <div className="flex flex-col gap-2 px-4">
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                  <Icon
                    type="far"
                    name="id-card"
                    className="text-sm text-body"
                  />
                </div>
                <span className="text-sm text-body-loud">Tipo de usuario:</span>
                <span className="text-sm text-body">
                  {person.type || "Natural"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                  <Icon
                    type="far"
                    name="id-card"
                    className="text-sm text-body"
                  />
                </div>
                <span className="text-sm text-body-loud">
                  Documento de identidad:
                </span>
                <span className="text-sm text-body">
                  {person.document_number}
                </span>
                <span className="text-sm text-body">
                  {person.document_type}
                </span>
              </div>

              {/* Documents */}
              {item.documents && item.documents.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                    <Icon
                      type="far"
                      name="file"
                      className="text-sm text-body mt-0.5"
                    />
                  </div>
                  <span className="text-sm text-body-loud">
                    Carga documental:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {item.documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Badge size="xs" variant="neutral" shape="square">
                          <Icon
                            type="far"
                            name="file"
                            className="text-body-quiet"
                          />
                          {doc.name}
                        </Badge>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Visibility */}
              {requestsVisibilityLabel && (
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                    <Icon type="far" name="eye" className="text-sm text-body" />
                  </div>
                  <span className="text-sm text-body">
                    {requestsVisibilityLabel}
                  </span>
                </div>
              )}
              <div className="w-full h-px border-t border-neutral-quiet" />
            </div>

            {typeof item.can_reassign === "boolean" && (
              <div className="flex items-center gap-2 mt-1 px-4">
                <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                  <Icon
                    type="far"
                    name="arrow-right-arrow-left"
                    className="text-sm text-body"
                  />
                </div>
                <span className="text-sm text-body-loud">
                  Permiso para reasignar:
                </span>
                <span className="text-sm text-body">
                  {item.can_reassign ? "Sí" : "No"}
                </span>
              </div>
            )}

            {/* Association status */}
            <div className="flex flex-col gap-2 mt-1 px-4">
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                  <Icon
                    type="far"
                    name="user-group"
                    className="text-sm text-body"
                  />
                </div>
                <span className="text-sm text-body-loud">
                  Estado del vínculo:
                </span>
                {statusBadge(
                  item.authorization_status,
                  item.authorization_status_label,
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                  <Icon
                    type="far"
                    name="calendar-check"
                    className="text-sm text-body"
                  />
                </div>
                <span className="text-sm text-body-loud">
                  Fecha de asociación:
                </span>
                <span className="text-sm text-body">
                  {formatDate(
                    item.authorization_approved_at || item.created_at,
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                  <Icon
                    type="far"
                    name="calendar-times"
                    className="text-sm text-body"
                  />
                </div>
                <span className="text-sm text-body-loud">
                  Fecha de vencimiento:
                </span>
                <span className="text-sm text-body">
                  {formatDate(item.authorization_ended_at)}
                </span>
              </div>
              {isUnlinked && (
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                    <Icon
                      type="far"
                      name="calendar-xmark"
                      className="text-sm text-body"
                    />
                  </div>
                  <span className="text-sm text-body-loud">
                    Fecha de desvinculación:
                  </span>
                  <span className="text-sm text-body">
                    {formatDate(item.unlinked_at)}
                  </span>
                </div>
              )}
              {isUnlinked && item.unlinked_by && (
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                    <Icon
                      type="far"
                      name="user-slash"
                      className="text-sm text-body"
                    />
                  </div>
                  <span className="text-sm text-body-loud">
                    Desvinculado por:
                  </span>
                  <span className="text-sm text-body">
                    {unlinkedByText(item.unlinked_by)}
                  </span>
                </div>
              )}
              {isUnlinked && item.unlink_reason && (
                <div className="flex items-start gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                    <Icon
                      type="far"
                      name="comment"
                      className="text-sm text-body mt-0.5"
                    />
                  </div>
                  <span className="text-sm text-body-loud">
                    Motivo de desvinculación:
                  </span>
                  <span className="text-sm text-body">
                    {item.unlink_reason}
                  </span>
                </div>
              )}
              <div className="w-full h-px border-t border-neutral-quiet" />
            </div>

            {/* Request link */}
            {item.formality_request && (
              <div className="flex items-center justify-between mt-1 px-4">
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral">
                    <Icon
                      type="far"
                      name="box-ballot"
                      className="text-sm text-body"
                    />
                  </div>
                  <span className="text-sm text-body-loud">
                    Solicitud en la que se realizó la asociación:
                  </span>
                  <a
                    href={`/tramite/${item.formality_request.id}`}
                    className="text-sm text-action underline"
                  >
                    {item.formality_request.code}
                  </a>
                </div>
                <a
                  href={`/tramite/${item.formality_request.id}`}
                  className="flex items-center gap-1 text-sm text-action"
                >
                  Ver solicitud
                  <Icon type="far" name="arrow-up-right" className="text-xs" />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
